import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Row, Col, Card, InputNumber, Space, Table, Alert, Spin, Tag, Button, Tooltip,
  Switch,
} from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../libs/api';
import { computeForecast, aggregateByPeriod, PURCHASE_TERMS } from '../../utils/financialPlanning';
import { NEUTRAL, INK, UNDER, fmtMoney } from './shared';

/**
 * PW Renewable's gross profit model.
 *
 * The same engine Financial Planning runs for PW, with opening balances, the
 * balance sheet and cashflow tables, and the start-month and horizon selectors
 * removed — the window is Cashflow Control's, so that the model and the page it
 * feeds cannot end up describing different months.
 *
 * PW buys stock and resells it, so gross profit is a margin on sales rather
 * than a pipeline. The purchase terms still matter and are still here: they do
 * not touch the P&L, but they decide when the money for that stock actually
 * leaves, and the statements use the engine's cash timing rather than assuming
 * everything settles in the month it is incurred.
 */

const moneyFmt = (v) => {
  if (v == null || v === '') return '';
  const num = parseFloat(v);
  if (Number.isNaN(num)) return String(v);
  const parts = (Math.round(num * 100) / 100).toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$ ${parts.join('.')}`;
};
const moneyParse = (v) => v.replace(/\$\s?|,/g, '');

const PURCHASE_LABELS = {
  A: `10% deposit · ${PURCHASE_TERMS.A.shippingDays}d shipping · balance ${PURCHASE_TERMS.A.balanceDaysAfterShipping}d after`,
  B: `20% deposit · ${PURCHASE_TERMS.B.shippingDays}d shipping · balance ${PURCHASE_TERMS.B.balanceDaysAfterShipping}d after`,
  C: `20% deposit · ${PURCHASE_TERMS.C.shippingDays}d shipping · balance ${PURCHASE_TERMS.C.balanceDaysAfterShipping}d after`,
};

const EMPTY_DRIVER = { sales: 0, purchaseA: 0, purchaseB: 0, purchaseC: 0 };

/**
 * What to buy each month, given what is being sold and what is on the shelf.
 *
 * A stock business does not choose its purchases freely: it buys what it is
 * about to sell, adjusted for the stock it holds. So
 *
 *     purchase = cost of sales this month
 *              + the stock needed to cover next month
 *              - the stock already held
 *
 * carried forward month by month, so buying in one month reduces the next
 * rather than every month repeating the same figure.
 *
 * Two things it will not decide. The target cover is measured, not chosen — it
 * holds the cover the business already runs at, because telling PW how much
 * stock to carry is not this page's job. And it never returns a negative
 * purchase: the answer to being overstocked is to buy nothing, not to un-buy.
 *
 * A plain function rather than a hook, because the first fill needs it before
 * any driver has been typed and the button needs it afterwards. One
 * implementation, so the number the page opens on and the number the button
 * produces cannot differ.
 */
function planPurchases({ months, sales, cogsPct, stock }) {
  const coverMonths = stock.coverMonths != null && stock.coverMonths > 0 ? stock.coverMonths : 1;
  let held = stock.latest.cover;
  return months.map((m, i) => {
    const cogs = (sales[i] || 0) * cogsPct;
    const nextCogs = (i + 1 < months.length ? sales[i + 1] : sales[i] || 0) * cogsPct;
    const purchase = Math.max(0, cogs + nextCogs * coverMonths - held);
    const opening = held;
    held = held + purchase - cogs;
    return {
      month: m,
      opening: Math.round(opening),
      cogs: Math.round(cogs),
      purchase: Math.round(purchase),
      closing: Math.round(held),
    };
  });
}

/**
 * What is already committed, and on what dates.
 *
 * A purchase driver is an *order* date: the engine pays the deposit the day the
 * order is placed, receives stock `shippingDays` later, and pays the balance
 * `balanceDaysAfterShipping` after that. Everything typed above is a new order.
 *
 * Which leaves orders placed before this window, in two kinds:
 *
 *   Already invoiced — the supplier has billed and it sits in accounts payable.
 *   Read from Xero, scheduled on each due date.
 *
 *   Ordered, deposit paid, not yet shipped — no invoice exists, because the
 *   supplier bills on shipping. Xero has no document to read, but it does have
 *   the deposit: what it was, and the day it left the bank. A deposit is a
 *   known fraction of an order, and the balance falls due a fixed period after
 *   it. So the amount is inferred from the term and the date is measured.
 *
 * Deposits are matched newest first against the balance still on the books,
 * because deposits are released as stock arrives and the ones still sitting
 * there are the most recent payments. Anything the bank cannot explain is
 * reported rather than given an invented date.
 */
function OrdersInFlight({ stock, outstanding, error, included, onToggle }) {
  const apTotal = outstanding.ap.reduce((t, i) => t + (Number(i.amountDue) || 0), 0);
  const arTotal = outstanding.ar.reduce((t, i) => t + (Number(i.amountDue) || 0), 0);
  const open = stock?.openOrders;
  const balanceTotal = (open?.orders || []).reduce((t, o) => t + o.balance, 0);

  if (error) {
    return (
      <Alert
        type="warning" showIcon style={{ marginTop: 10 }}
        message={(
          <span style={{ fontSize: 12 }}>
            Outstanding invoices unavailable — {error}. The forecast then assumes nobody owes PW
            anything and PW owes nobody, which will overstate cash.
          </span>
        )}
      />
    );
  }

  return (
    <div style={{ marginTop: 12, fontSize: 11, color: NEUTRAL, lineHeight: 1.7 }}>
      <div>
        {/* Not yet read is not the same as nothing owed, and $0 says the
            second. The invoice read waits until the model has to run. */}
        {outstanding.loaded ? (
          <>
            Already committed:{' '}
            <b>{fmtMoney(apTotal)}</b> of supplier invoices outstanding
            {' and '}<b>{fmtMoney(arTotal)}</b> owed by customers, each scheduled on its own
            due date.
          </>
        ) : (
          <>
            Outstanding invoices have not been read yet — they load the moment anything here is
            changed. The saved forecast already has them inside it.
          </>
        )}
      </div>

      {stock?.depositsError && (
        <Alert
          type="warning" showIcon style={{ marginTop: 8 }}
          message={(
            <span style={{ fontSize: 12 }}>
              Deposit payment dates unavailable — {stock.depositsError}. The balance owing on
              orders already placed cannot be scheduled, so cash out is understated.
            </span>
          )}
        />
      )}

      {open?.orders?.length > 0 && (
        <Card
          size="small"
          style={{ marginTop: 10 }}
          styles={{ body: { padding: '10px 12px' } }}
          title={(
            <span style={{ fontSize: 12 }}>
              Balance owing on orders already placed — <b>{fmtMoney(balanceTotal)}</b>
            </span>
          )}
          extra={(
            <Space size={8}>
              <span style={{ fontSize: 11, color: NEUTRAL }}>in the forecast</span>
              <Switch size="small" checked={included} onChange={onToggle} />
            </Space>
          )}
        >
          <div style={{ fontSize: 11, color: NEUTRAL, marginBottom: 8 }}>
            {fmtMoney(open.matched)} of deposits traced to the payments below. The supplier bills
            on shipping, so no invoice exists yet — the amount comes from term B&apos;s{' '}
            {Math.round(open.terms.depositPct * 100)}% deposit and the due date from the day the
            deposit actually left, plus {open.terms.shippingDays}d shipping and{' '}
            {open.terms.balanceDaysAfterShipping}d terms.
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey={(o) => `${o.depositDate}:${o.deposit}`}
            dataSource={open.orders}
            style={{ maxWidth: 760 }}
            columns={[
              {
                title: 'Deposit paid', dataIndex: 'depositDate', width: 110,
                render: (v) => dayjs(v).format('D MMM YY'),
              },
              { title: 'Deposit', dataIndex: 'deposit', width: 110, align: 'right', render: fmtMoney },
              {
                title: 'Implied order', dataIndex: 'orderValue', width: 120, align: 'right',
                render: (v) => <span style={{ color: NEUTRAL }}>{fmtMoney(v)}</span>,
              },
              {
                title: 'Balance due', dataIndex: 'balance', width: 120, align: 'right',
                render: (v) => <b style={{ color: INK }}>{fmtMoney(v)}</b>,
              },
              {
                title: 'On', dataIndex: 'dueDate', width: 110,
                render: (v) => dayjs(v).format('D MMM YY'),
              },
            ]}
          />
          {open.unexplained > 1 && (
            <Alert
              type="warning" showIcon style={{ marginTop: 8 }}
              message={(
                <span style={{ fontSize: 12 }}>
                  {fmtMoney(open.unexplained)} of the deposits balance has no bank payment in the
                  last six months to explain it — paid earlier, or paid some way other than from
                  the bank. Its timing is unknown, so it is not scheduled and the cash out below
                  is short by whatever balance it carries.
                </span>
              )}
            />
          )}
        </Card>
      )}
    </div>
  );
}

/**
 * The arithmetic behind the suggestion, on screen.
 *
 * A prefilled number nobody can take apart is a number nobody should trust,
 * and this one moves stock around for four months — so it shows its opening
 * balance, what it expects to consume, what it therefore buys, and where that
 * leaves the shelf.
 */
function StockWorking({ stock, suggestion, drivers }) {
  const money = (v) => fmtMoney(v);
  const typed = (i) => Number(drivers[i]?.purchaseB) || 0;
  return (
    <div style={{ marginTop: 12, fontSize: 11, color: NEUTRAL, lineHeight: 1.7 }}>
      <div>
        Stock at {dayjs(stock.latest.month).format('MMM YY')}:{' '}
        <b>{money(stock.latest.onHand)}</b> on hand
        {stock.latest.deposits ? <> plus <b>{money(stock.latest.deposits)}</b> paid to suppliers and not yet arrived</> : null}
        {' · '}
        {stock.coverMonths != null
          ? <>that is <b>{stock.coverMonths.toFixed(1)} months</b> of cover at the recent cost of sales, and the level the suggestion holds</>
          : <>no cost of sales history to measure cover against, so one month is assumed</>}
        {stock.avgPurchases ? <> · bought <b>{money(stock.avgPurchases)}</b> a month over the completed months</> : null}
      </div>
      <Table
        size="small"
        pagination={false}
        rowKey="month"
        style={{ marginTop: 8, maxWidth: 720 }}
        dataSource={suggestion.rows.map((r, i) => ({ ...r, typed: typed(i) }))}
        columns={[
          { title: 'Month', dataIndex: 'month', width: 90, render: (v) => dayjs(v).format('MMM YY') },
          { title: 'Opening stock', dataIndex: 'opening', width: 110, align: 'right', render: money },
          { title: 'Cost of sales', dataIndex: 'cogs', width: 110, align: 'right', render: money },
          {
            title: 'Suggested buy', dataIndex: 'purchase', width: 120, align: 'right',
            render: (v, r) => (
              <span style={{ color: Math.round(v) === Math.round(r.typed) ? UNDER : INK, fontWeight: 600 }}>
                {money(v)}
              </span>
            ),
          },
          { title: 'Closing stock', dataIndex: 'closing', width: 110, align: 'right', render: money },
        ]}
      />
    </div>
  );
}

export default function PwGpModel({
  startMonth, months, saved, stock, actuals, onForecast, renderSections,
  onOpenScenarios, loadRequest,
}) {
  const horizonMonths = months.length;

  const [cogsPct, setCogsPct] = useState(0.75);
  /**
   * Whether anyone has touched this model yet.
   *
   * The seeding used to be guarded by a "have I seeded" ref, which two
   * different code paths could claim: the measured figures and the fallback
   * baseline fetch. Whichever arrived first won, and when the fetch won it set
   * sales, left the purchases blank, and locked the measured pass out for good
   * — a page that opened half filled with the wrong margin.
   *
   * So the guard is now the only thing that actually matters: has a person
   * changed something. Until they have, the model re-derives itself from
   * whatever data has arrived, as often as that data changes. Afterwards it
   * never overwrites them.
   */
  const [touched, setTouched] = useState(false);
  const [drivers, setDrivers] = useState(months.map(() => ({ ...EMPTY_DRIVER })));
  const [baseline, setBaseline] = useState(null);
  /**
   * Invoices already issued and not yet settled, both directions.
   *
   * These were being passed to the engine as empty arrays. The engine knows
   * how to schedule them — it places each one on its due date — so the effect
   * was a forecast in which nobody owed PW anything and PW owed nobody: a
   * business starting from a clean slate every time the page loaded.
   */
  const [outstanding, setOutstanding] = useState({ ar: [], ap: [], loaded: false });
  const [outstandingError, setOutstandingError] = useState(null);
  /**
   * Whether the balance owing on orders already placed is in the forecast.
   *
   * On, because leaving several million of committed payments out of a cash
   * forecast is the larger error. Switchable, because the amount is inferred
   * from the supplier term rather than read from an invoice, and a number like
   * that should be one a person can take back out.
   */
  const [includeOpenOrders, setIncludeOpenOrders] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const touchedRef = useRef(false);

  /**
   * Mark the model touched, synchronously.
   *
   * The state flag alone is not enough to guard the seeding effects. Effects in
   * one commit run in declaration order and all share the render's closure, so
   * an effect that sets `touched` does not stop a later one from reading it as
   * false and seeding straight over what the first just applied. That is
   * exactly what happened to "load v1": the scenario landed, and the
   * saved-budget effect put the newest version back on top of it in the same
   * tick. The ref changes the instant it is set, so ordering stops mattering.
   */
  const markTouched = () => {
    touchedRef.current = true;
    setTouched(true);
  };

  const updateDriver = (i, key, val) => {
    markTouched();
    setDrivers((p) => p.map((d, j) => (j === i ? { ...d, [key]: val || 0 } : d)));
  };

  /**
   * Cost of sales as a share of revenue, over the three complete months on the
   * left of the table above.
   *
   * Taken from those figures rather than from a separate baseline endpoint, so
   * the number in the box can be checked by dividing two numbers already on
   * screen. Two endpoints would mean two definitions of cost of sales and no
   * way to tell which one the input came from.
   *
   * Weighted by month rather than an average of three monthly percentages: a
   * quiet month's margin should not count as much as a busy one's.
   */
  const measured = useMemo(() => {
    const ms = actuals?.months || [];
    if (!ms.length) return null;
    const sum = (key) => ms.reduce((t, m) => t + (Number(actuals.byMonth?.[m]?.[key]) || 0), 0);
    const revenue = sum('revenue');
    const cogs = sum('cogs');
    if (!(revenue > 0)) return null;
    return {
      months: ms,
      revenue,
      cogs,
      // The sales driver's starting point, from the same three months as the
      // margin. The baseline endpoint had its own average; two averages of the
      // same thing is one too many.
      avgRevenue: revenue / ms.length,
      cogsPct: cogs / revenue,
      gpPct: ((revenue - cogs) / revenue) * 100,
    };
  }, [actuals]);

  /**
   * What was last saved, and how to put it back — but not automatically.
   *
   * It used to load itself, on the reasoning that a saved number is someone's
   * decision. The trouble is that the first thing ever saved for PW carried a
   * hardcoded 75% placeholder from before any of this was measured, and
   * restoring it meant the page opened on a figure nobody had ever chosen and
   * which was twenty points off the real one.
   *
   * So the page opens calibrated to what actually happened, and the saved
   * version is a button. Nothing is lost and nothing is silently preferred.
   */
  const savedSet = useMemo(() => {
    const first = months.map((m) => saved?.[m]).find((x) => x && x.assumptions);
    if (!first) return null;
    /**
     * A scenario saved by another entity's model is not a scenario this one can
     * read. Its drivers carry different fields — systems and installs rather
     * than sales and purchase orders — and seeding from them fills every box
     * with undefined. Belt to the braces of clearing the data on switch.
     */
    if (first.assumptions.model && first.assumptions.model !== 'pw') return null;
    return {
      version: first.version,
      savedAt: first.savedAt,
      savedBy: first.savedBy,
      note: first.note,
      cogsPct: first.assumptions.cogsPct,
      byMonth: saved || {},
    };
  }, [saved, months]);

  /**
   * Apply a scenario chosen in the drawer.
   *
   * Months the scenario never covered keep whatever is on screen: a scenario
   * that did not forecast December is not a scenario that forecast December at
   * zero, and blanking the row would assert the second.
   */
  useEffect(() => {
    if (!loadRequest?.scenario) return;
    const sc = loadRequest.scenario;
    const a = Object.values(sc.byMonth)[0]?.assumptions || {};
    markTouched();
    if (a.cogsPct != null) setCogsPct(a.cogsPct);
    setDrivers((prev) => months.map((m, i) => sc.byMonth[m]?.drivers || prev[i]));
  }, [loadRequest, months]);

  /**
   * What to buy, given what is being sold and what is already on the shelf.
   *
   * A stock business does not choose its purchases freely: it buys what it is
   * about to sell, adjusted for the stock it is holding. So
   *
   *     purchase = cost of sales this month
   *              + the stock needed to cover next month
   *              - the stock already held
   *
   * carried forward month by month, so a purchase in one month reduces the next
   * one rather than every month suggesting the same figure.
   *
   * Two things it deliberately does not do. It does not choose a target cover —
   * it holds the cover the business already runs at, measured from Xero, because
   * telling PW how much stock to carry is not this page's job. And it never
   * suggests a negative purchase: the answer to being overstocked is to buy
   * nothing, not to un-buy.
   *
   * Stock "on hand" includes supplier deposits. Containers paid for and still
   * at sea are cover against next month's sales, and leaving them out tells you
   * to buy the same stock twice.
   */
  const suggestion = useMemo(() => {
    if (!stock || stock.error || !stock.latest) return null;
    return {
      coverMonths: stock.coverMonths != null && stock.coverMonths > 0 ? stock.coverMonths : 1,
      rows: planPurchases({
        months,
        sales: months.map((_, i) => Number(drivers[i]?.sales) || 0),
        cogsPct,
        stock,
      }),
    };
  }, [stock, drivers, cogsPct, months]);

  const applySuggestion = () => {
    if (!suggestion) return;
    markTouched();
    setDrivers((prev) => prev.map((d, i) => ({
      ...d,
      purchaseB: suggestion.rows[i] ? suggestion.rows[i].purchase : d.purchaseB,
    })));
  };

  /**
   * Fill it in once, when nothing has been saved and nothing typed.
   *
   * Only once: after that the number is someone's, and a background
   * recalculation that quietly replaced it would lose a decision without
   * saying so. The button re-applies it on request.
   */
  /**
   * Fill the margin in once, from what actually happened.
   *
   * Once only, for the same reason the purchase suggestion fills once: after
   * the first fill the number is a decision someone made, and a background
   * recalculation that replaced it would lose that without saying so.
   */
  /**
   * Both read through refs by the baseline fetch. That callback is a dependency
   * of the effect that runs it, so closing over either value would refetch
   * every time it changed.
   */
  const measuredRef = useRef(null);
  useEffect(() => { measuredRef.current = measured; }, [measured]);
  const savedRef = useRef(null);
  useEffect(() => { savedRef.current = savedSet; }, [savedSet]);

  /**
   * Fill the whole model in as soon as there is something to fill it from.
   *
   * Margin and sales come from the measured months; the purchase follows from
   * those two and the stock on hand, so it is computed here rather than waiting
   * for a second pass — a page that arrives with sales filled and purchases
   * blank is a page that still has to be clicked before it means anything.
   *
   * Once, and only while untouched. After that every number is somebody's, and
   * a recalculation in the background would replace a decision without saying
   * so; the buttons re-apply on request.
   */
  /**
   * What the page opens on: the budget, if one has been agreed.
   *
   * A saved version is the forecast someone committed to, and that is what
   * belongs on screen — not a fresh calibration that happens to describe the
   * last three months. Only where nothing has been saved does the measured
   * average fill the gap, and the header says which of the two you have.
   */
  useEffect(() => {
    if (touchedRef.current || !savedSet) return;
    if (savedSet.cogsPct != null) setCogsPct(savedSet.cogsPct);
    setDrivers((prev) => months.map((m, i) => savedSet.byMonth?.[m]?.drivers || prev[i]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched, savedSet, months]);

  useEffect(() => {
    if (touchedRef.current || savedSet || !measured) return;

    const pct = Math.max(0, Math.min(1, measured.cogsPct));
    const sales = Math.round(measured.avgRevenue);
    // Computed here from the margin and sales just derived, not read off the
    // `suggestion` memo — that one follows the driver state, which has not been
    // updated yet in this pass. A page arriving with sales filled and purchases
    // blank is a page that still needs clicking before it means anything.
    const purchases = stock && !stock.error && stock.latest
      ? planPurchases({ months, sales: months.map(() => sales), cogsPct: pct, stock })
      : null;

    setCogsPct(pct);
    setDrivers((prev) => prev.map((d, i) => ({
      ...d,
      sales,
      purchaseB: purchases ? purchases[i].purchase : d.purchaseB,
    })));
    // Deliberately not depending on drivers or cogsPct: this sets them, and
    // depending on them would re-run on its own output.
  }, [measured, stock, months, touched, savedSet]);

  const fetchBaseline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/financial-planning/opex-baseline', {
        params: { entity: 'pw', month: dayjs(startMonth).format('YYYY-MM') },
      });
      const d = res.data.data;
      setBaseline(d);
      /**
       * Only a fallback now. Sales and margin are measured from the actual
       * columns of the table above; this fills them in when that read failed,
       * so a Xero outage on one call does not leave the model empty.
       *
       * Read through a ref rather than closing over the value: this callback is
       * a dependency of the effect that runs it, so depending on the measured
       * figure would refetch every time it changed.
       */
      if (!measuredRef.current && !touchedRef.current && !savedRef.current) {
        if (d.monthlyRevenue) {
          setDrivers((prev) => prev.map((row) => ({ ...row, sales: d.monthlyRevenue })));
        }
        /**
         * A margin of zero or a hundred percent is not a margin, it is a read
         * that went wrong — this endpoint has its own section matcher and can
         * classify a cost as income. Better to leave the box alone and let the
         * label say nothing was measured than to fill it with an impossible
         * number that looks authoritative.
         */
        const pct = d.avgGpPct != null ? (100 - d.avgGpPct) / 100 : null;
        if (pct != null && pct > 0 && pct < 1) setCogsPct(pct);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the PW baseline from Xero');
    }
    setLoading(false);
  }, [startMonth]);

  /**
   * Only fetch what the screen actually needs.
   *
   * With a saved budget on screen, none of this is on it: the figures come
   * from the saved outputs, the drivers and assumptions from the saved
   * scenario. These reads exist to seed a model that has not been asked to run
   * yet — and they are Xero calls, several at once, which is what put the page
   * over Xero's five-concurrent limit and made it report the profit and loss
   * unavailable.
   *
   * So they wait until somebody touches something, which is the moment the
   * model has to produce numbers of its own.
   */
  const needsModel = !savedSet || touched;
  useEffect(() => { if (needsModel) fetchBaseline(); }, [needsModel, fetchBaseline]);

  const fetchOutstanding = useCallback(async () => {
    setOutstandingError(null);
    try {
      const res = await api.get('/api/financial-planning/invoices', { params: { entity: 'pw' } });
      setOutstanding({ ar: res.data.data?.ar || [], ap: res.data.data?.ap || [], loaded: true });
    } catch (err) {
      setOutstandingError(err.response?.data?.message || 'Could not read outstanding invoices from Xero');
      setOutstanding({ ar: [], ap: [], loaded: false });
    }
  }, []);

  useEffect(() => { if (needsModel) fetchOutstanding(); }, [needsModel, fetchOutstanding]);

  /**
   * Orders placed but not yet shipped, as payables the engine can schedule.
   *
   * The engine already knows how to place an invoice on its due date; these
   * are the same shape. What they are not is invoices — the supplier bills on
   * shipping, so nothing has been issued yet and the amount comes from the
   * term rather than from a document. The date is real: it counts from the day
   * the deposit left the bank.
   */
  const openOrderAp = useMemo(() => {
    const orders = stock?.openOrders?.orders;
    if (!includeOpenOrders || !orders?.length) return [];
    return orders.map((o) => ({
      contactName: o.reference || 'Order in transit',
      invoiceNumber: `deposit ${dayjs(o.depositDate).format('D MMM')}`,
      dueDate: o.dueDate,
      amountDue: o.balance,
      total: o.orderValue,
      inferred: true,
    }));
  }, [stock, includeOpenOrders]);

  const forecast = useMemo(() => computeForecast({
    openingBalances: {
      cash: 0, accountsReceivable: 0, inventory: 0, depositsToSuppliers: 0,
      otherAssets: 0, accountsPayable: 0, otherLiabilities: 0, gstPayable: 0,
    },
    monthlyDrivers: drivers,
    cogsPct,
    // Opex is Opex Control's number. Passing zero keeps this model out of an
    // argument it should not be having with the budget the Opex tab inherits.
    monthlyOpex: 0,
    horizonMonths,
    existingAR: outstanding.ar,
    existingAP: [...outstanding.ap, ...openOrderAp],
    startDate: dayjs(startMonth).startOf('month').format('YYYY-MM-DD'),
  }), [drivers, cogsPct, horizonMonths, startMonth, outstanding, openOrderAp]);

  const periods = useMemo(() => aggregateByPeriod(forecast.days, 'month'), [forecast]);

  useEffect(() => {
    const byMonth = {};
    for (const p of periods) {
      byMonth[`${p.key}-01`] = {
        revenue: p.pl.revenue,
        cogs: p.pl.cogs,
        gp: p.pl.gp,
        cashIn: p.cf.cashIn,
        cashOutPurchases: p.cf.cashOutPurchases,
      };
    }
    onForecast({
      byMonth,
      driversByMonth: Object.fromEntries(months.map((m, i) => [m, drivers[i] || { ...EMPTY_DRIVER }])),
      assumptions: { model: 'pw', cogsPct },
      // Whether anyone has changed the inputs. The page needs it to decide
      // whether the figures on screen are still the saved budget.
      touched,
    });
  }, [periods, months, drivers, cogsPct, touched, onForecast]);


/**
 * The model hands its sections back rather than laying them out.
 *
 * Cashflow Control interleaves them with its own output table — drivers, then
 * the result, then the assumptions behind it, then the pipeline — and a
 * component that renders its own column cannot be interleaved with anything.
 * Holding the state here and the order there keeps both where they belong.
 */
  const sections = {
    alerts: error ? (
      <Alert type="warning" showIcon message={error} />
    ) : null,
    assumptions: (
      <Card
              size="small"
              title="Assumptions"
              extra={savedSet === null ? (
                <Tag style={{ margin: 0 }}>
                  nothing saved — showing the last 3 months&apos; average
                </Tag>
              ) : (
                <Space size={8}>
                  <Tag color="blue" style={{ margin: 0 }}>
                    showing v{savedSet.version}, saved {dayjs(savedSet.savedAt).format('D MMM')}
                    {savedSet.savedBy ? ` by ${savedSet.savedBy}` : ''}
                  </Tag>
                  {/* The saved version is a click away rather than the default.
                      It used to load itself, which meant the page opened on
                      whatever was saved first — including a hardcoded
                      placeholder margin from before any of this was measured. */}
                  <Tooltip title="Choose a saved scenario, read what is in it, then load it.">
                    <Button size="small" onClick={onOpenScenarios}>load saved</Button>
                  </Tooltip>
                </Space>
              )}
            >
              <Spin spinning={loading}>
                <Row gutter={[16, 12]}>
                  <Col xs={24} md={8}>
                    <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 4 }}>Cost of sales as % of revenue</div>
                    <InputNumber
                      value={Math.round(cogsPct * 1000) / 10}
                      onChange={(v) => {
                        markTouched();
                        setCogsPct(Math.max(0, Math.min(1, (v || 0) / 100)));
                      }}
                      style={{ width: '100%' }} min={0} max={100} step={0.1}
                      formatter={(v) => `${v}%`} parser={(v) => v.replace('%', '')}
                    />
                    {measured && Math.abs(measured.cogsPct - cogsPct) >= 0.0005 && (
                      <Button
                        type="link" size="small"
                        style={{ padding: 0, height: 'auto', fontSize: 11 }}
                        onClick={() => { markTouched(); setCogsPct(measured.cogsPct); }}
                      >
                        reset to the last 3 months
                      </Button>
                    )}
                    <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 4 }}>
                      gross margin {((1 - cogsPct) * 100).toFixed(1)}%
                      {measured ? (
                        <Tooltip
                          title={`${fmtMoney(measured.cogs)} of cost on ${fmtMoney(measured.revenue)} of revenue across ${measured.months.map((m) => dayjs(m).format('MMM')).join(', ')} — the actual columns above`}
                        >
                          <span>
                            {' · '}
                            <b>{(measured.cogsPct * 100).toFixed(1)}%</b> over the last 3 complete
                            months{Math.abs(measured.cogsPct - cogsPct) < 0.0005 ? ' (filled in)' : ''}
                          </span>
                        </Tooltip>
                      ) : (
                        baseline?.avgGpPct != null && ` · 3-mo GP ${baseline.avgGpPct}% from Xero`
                      )}
                    </div>
                  </Col>
                  <Col xs={24} md={16}>
                    <div style={{ fontSize: 12, color: NEUTRAL, marginBottom: 4 }}>
                      {/* The same figures as the actual columns of the table
                          above, not a second endpoint's version of them. Two
                          sets of "the last three months" on one card is how a
                          reader ends up unable to say where an input came
                          from. */}
                      The last 3 complete months, from the table above — what the drivers are
                      filled from
                    </div>
                    <Space size={20} wrap>
                      <div>
                        <div style={{ fontSize: 11, color: NEUTRAL }}>Avg monthly revenue</div>
                        <div style={{ fontSize: 16 }}>
                          {measured ? fmtMoney(measured.avgRevenue) : '—'}
                        </div>
                      </div>
                      {measured ? measured.months.map((m) => {
                        const a = actuals.byMonth?.[m];
                        const gp = a?.revenue ? ((a.revenue - a.cogs) / a.revenue) * 100 : null;
                        return (
                          <div key={m}>
                            <div style={{ fontSize: 11, color: NEUTRAL }}>{dayjs(m).format('MMM YY')}</div>
                            <div style={{ fontSize: 13 }}>{fmtMoney(a?.revenue)}</div>
                            <div style={{ fontSize: 11, color: NEUTRAL }}>
                              GP {gp == null ? '—' : `${gp.toFixed(1)}%`}
                            </div>
                          </div>
                        );
                      }) : (
                        <div style={{ fontSize: 12, color: NEUTRAL }}>
                          No actual months available — the drivers fall back to Xero&apos;s own
                          three-month baseline.
                        </div>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Spin>
            </Card>
    ),
    drivers: (
      <Card
              size="small"
              title="Drivers — sales and stock purchases per month"
              extra={suggestion ? (
                <Tooltip title="Recompute Purchase B from the sales above, the stock on hand, and the cover PW currently runs at.">
                  <Button size="small" icon={<ThunderboltOutlined />} onClick={applySuggestion}>
                    fill Purchase B from sales &amp; stock
                  </Button>
                </Tooltip>
              ) : null}
            >
              <Table
                size="small"
                pagination={false}
                scroll={{ x: 760 }}
                dataSource={drivers.map((d, i) => ({ key: i, ...d, monthLabel: dayjs(months[i]).format('MMM YYYY') }))}
                columns={[
                  { title: 'Month', dataIndex: 'monthLabel', width: 110, fixed: 'left' },
                  {
                    title: 'Sales', dataIndex: 'sales', width: 170,
                    render: (v, rec) => (
                      <InputNumber
                        value={v} onChange={(val) => updateDriver(rec.key, 'sales', val)}
                        style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse}
                      />
                    ),
                  },
                  ...['A', 'B', 'C'].map((t) => ({
                    title: (
                      <span>
                        Purchase <Tag style={{ marginLeft: 2 }}>{t}</Tag>
                      </span>
                    ),
                    dataIndex: `purchase${t}`,
                    width: 170,
                    render: (v, rec) => (
                      <InputNumber
                        value={v} onChange={(val) => updateDriver(rec.key, `purchase${t}`, val)}
                        style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse}
                      />
                    ),
                  })),
                ]}
              />
              {suggestion && <StockWorking stock={stock} suggestion={suggestion} drivers={drivers} />}
              <OrdersInFlight
                stock={stock}
                outstanding={outstanding}
                error={outstandingError}
                included={includeOpenOrders}
                onToggle={setIncludeOpenOrders}
              />
              {stock?.error && (
                <Alert
                  type="warning" showIcon style={{ marginTop: 10 }}
                  message={(
                    <span style={{ fontSize: 12 }}>
                      Stock levels unavailable — {stock.error}. Purchase B is left alone rather
                      than suggested from nothing.
                    </span>
                  )}
                />
              )}
              <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 10, lineHeight: 1.7 }}>
                Purchases do not change gross profit — cost of sales is the margin on what is sold. They
                decide when the money for that stock leaves, which is what the cashflow statement uses.
                <br />
                <b>A:</b> {PURCHASE_LABELS.A}<br />
                <b>B:</b> {PURCHASE_LABELS.B}<br />
                <b>C:</b> {PURCHASE_LABELS.C}
              </div>
            </Card>
    ),
    // PW sells stock, not projects. There is no pipeline to show.
    pipeline: null,
  };

  if (renderSections) return renderSections(sections);
  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      {sections.alerts}
      {sections.assumptions}
      {sections.drivers}
    </Space>
  );
}
